import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.css";
import "./Hero.css";
import { MoveRight } from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "./Navbar";

const slides = [
  require("../assets/homehero/hero1.jpeg"),
  require("../assets/homehero/hero2.jpg"),
  require("../assets/homehero/hero3.jpg"),
  require("../assets/homehero/hero4.jpg"),
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    slides.forEach((slide) => {
      const img = new Image();
      img.src = slide;
    });

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full h-screen overflow-hidden font-sans bg-ink-900">
      {/* Slideshow Background */}
      <div className="absolute inset-0 z-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[1500ms] ease-in-out ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
            style={{ 
              backgroundImage: `url(${slide})`,
            }}
          />
        ))}
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/95 via-ink-900/60 to-ink-900/40 z-10" />
      </div>

      {/* Navbar */}
      <Navbar isTransparent={true} />

      {/* Hero Content - Bottom Aligned */}
      <div className="absolute inset-0 z-20 px-6 md:px-12 flex flex-col justify-center items-start">
        
        {/* Main Text */}
        <div className="w-full max-w-5xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20"
          >
            <span className="w-2 h-2 rounded-full bg-tropical-400 animate-pulse"></span>
            <span className="text-xs md:text-sm font-medium text-white tracking-widest uppercase font-display">
              St. John · US Virgin Islands
            </span>
          </motion.div>

          <motion.h1 
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.9] tracking-tighter font-display mb-8"
          >
            340 Real Estate <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-tropical-300 to-white/70">
              Company.
            </span>
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl md:text-2xl text-white/70 font-light max-w-2xl mb-12 leading-relaxed"
          >
            Island wise since 1978 and Real Estate agent since 2000. 340RECO since 2013.
          </motion.p>
          
          <motion.div 
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row flex-wrap items-center gap-6"
          >
            <Link
              to="/properties"
              className="group px-8 py-4 bg-white text-ink-900 text-lg font-bold rounded-full hover:bg-tropical-500 hover:text-white transition-all duration-500 flex items-center gap-3 shadow-[0_0_40px_rgba(255,255,255,0.2)] hover:shadow-[0_0_40px_rgba(45,212,191,0.4)]"
            >
              Explore Listings <MoveRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              to="/mls"
              className="group px-8 py-4 bg-white/10 backdrop-blur-md border border-white/20 text-white text-lg font-bold rounded-full hover:bg-white hover:text-ink-900 transition-all duration-500 flex items-center gap-3"
            >
              Search MLS <MoveRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <div className="flex items-center gap-6">
               <div className="flex flex-col">
                  <span className="text-xs text-white/40 uppercase tracking-widest">Direct Line</span>
                  <a href="tel:+13406436068" className="text-white hover:text-tropical-400 transition-colors font-medium">340-643-6068</a>
               </div>
               <div className="w-px h-10 bg-white/20"></div>
               <div className="flex flex-col">
                  <span className="text-xs text-white/40 uppercase tracking-widest">Office</span>
                  <a href="tel:+13407794478" className="text-white hover:text-tropical-400 transition-colors font-medium">340-779-4478</a>
               </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Gradient Blob */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] bg-tropical-500/20 blur-[120px] rounded-full z-10 pointer-events-none" />


      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              index === currentSlide ? "w-8 bg-white" : "w-2 bg-white/30"
            }`}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Hero;

